import type { BoardData, Item, PropertyDef, PropValue } from '../model/schema.js';

/**
 * Custom properties (decision U3). Definitions live in `meta.propertyDefs`, values in
 * `Item.props[key]`. Keys used by the system itself are reserved and never offered to users.
 */
export const RESERVED_PROP_KEYS: readonly string[] = ['groupid', 'adapter', 'passthrough'];

/** True for keys the system uses internally (`suppress:<rule>`, group ids, adapter flags …). */
export function isSystemProp(key: string): boolean {
  return key.includes(':') || RESERVED_PROP_KEYS.includes(key.toLowerCase());
}

/** Lower-case snake key from a label, e.g. "Owner (rental)" → "owner_rental". */
export function propKeyFromLabel(label: string): string {
  const base = label
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40)
    .replace(/_+$/, '');
  return base === '' ? 'prop' : base;
}

export type DefError = 'key-invalid' | 'key-reserved' | 'key-duplicate' | 'options-missing';

/** Checks a definition against the existing ones (returns the first problem, if any). */
export function checkPropertyDef(
  def: PropertyDef,
  existing: readonly PropertyDef[],
): DefError | null {
  if (!/^[a-z][a-z0-9_]{0,39}$/.test(def.key)) return 'key-invalid';
  if (isSystemProp(def.key)) return 'key-reserved';
  if (existing.some((d) => d.key === def.key)) return 'key-duplicate';
  if (def.type === 'select' && (def.options ?? []).length === 0) return 'options-missing';
  return null;
}

export function propertyDefs(data: BoardData): PropertyDef[] {
  return data.meta.propertyDefs ?? [];
}

/**
 * Parses user input for a property. Returns `undefined` to clear the value and `null` when the
 * text is not valid for the type (the caller keeps the old value and shows an error).
 */
export function parsePropValue(text: string, def: PropertyDef): PropValue | undefined | null {
  const t = text.trim();
  if (t === '') return undefined;
  switch (def.type) {
    case 'number': {
      const n = Number(t.replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    case 'boolean': {
      const v = t.toLowerCase();
      if (['true', 'yes', 'ja', 'x', '1', 'y', 'j'].includes(v)) return true;
      if (['false', 'no', 'nein', '0', 'n'].includes(v)) return false;
      return null;
    }
    case 'select':
      return (def.options ?? []).find((o) => o.toLowerCase() === t.toLowerCase()) ?? null;
    case 'text':
      return t;
  }
}

/** Display text of an item's value for a property ('' when unset). */
export function formatPropValue(item: Item, def: PropertyDef, locale: 'en' | 'de' = 'en'): string {
  const v = item.props[def.key];
  if (v === undefined) return '';
  if (typeof v === 'boolean')
    return v ? (locale === 'de' ? 'ja' : 'yes') : locale === 'de' ? 'nein' : 'no';
  return String(v);
}

/** Ad-hoc (undefined) user props of an item: keys that are neither defined nor system keys. */
export function adHocProps(item: Item, defs: readonly PropertyDef[]): [string, PropValue][] {
  const defined = new Set(defs.map((d) => d.key));
  return Object.entries(item.props)
    .filter(([k]) => !defined.has(k) && !isSystemProp(k))
    .sort(([a], [b]) => a.localeCompare(b));
}

/** Removes a definition and (optionally) its values from all items. Pure: returns a new board. */
export function removePropertyDef(data: BoardData, key: string, dropValues: boolean): BoardData {
  const meta = { ...data.meta, propertyDefs: propertyDefs(data).filter((d) => d.key !== key) };
  if (!dropValues) return { ...data, meta };
  const items = Object.fromEntries(
    Object.entries(data.items).map(([id, i]) => {
      if (!(key in i.props)) return [id, i];
      const { [key]: _drop, ...props } = i.props;
      return [id, { ...i, props }];
    }),
  );
  return { ...data, meta, items };
}

/**
 * Extra list/CSV columns for the defined custom properties. `itemOf` maps a row to the item the
 * values come from (e.g. an input-list row → its source item). Labels are the user's own text.
 */
export function customPropColumns<R>(
  defs: readonly PropertyDef[],
  itemOf: (row: R) => Item | undefined,
  locale: 'en' | 'de' = 'en',
): { key: string; label: string; value: (row: R) => string }[] {
  return defs.map((def) => ({
    key: `prop:${def.key}`,
    label: def.label,
    value: (row: R) => {
      const item = itemOf(row);
      return item === undefined ? '' : formatPropValue(item, def, locale);
    },
  }));
}
